import { TurfServiceService } from '../turf-service/turf-service.service';
import { UserManagerService } from '../userManager/user-manager.service';
import { Injectable } from '@angular/core';
import { Client, IMessage } from '@stomp/stompjs';
import { ConfigService } from '../config/config.service';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { environment, RABBIT_CONFIG } from 'src/environments/environment';
import { Cagnotte } from 'src/models/race-data.model';
import { CAGNOTTE_MSG } from 'src/sample-data/web-socket-msg.data';
import { LoggerService } from '../logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  getCagnote = new BehaviorSubject<Cagnotte[]>([]);
  checkIdletime = false;
  subscription: Subscription = new Subscription();

  private wsClient: Client;
  private messageSubject = new Subject<any>();
  private isConnectedForRaces = false;
  private isConnectedForSession = false;
  private cagnotteInterval?: number;

  constructor(
    private configs: ConfigService,
    private userServices: UserManagerService,
    private turfService: TurfServiceService,
    private logger: LoggerService
  ) {
    this.wsClient = new Client({
      brokerURL: environment.API.rabbitAddress,
      reconnectDelay: 5000,
      connectHeaders: {
        login: RABBIT_CONFIG.USER,
        passcode: RABBIT_CONFIG.PASSWD,
      },
    });

    // Setup connection handlers
    this.wsClient.onConnect = (frame) => {
      // console.log('WebSocket connected:', frame);
      this.checkIdletime = false;

      // Always subscribe to races when connected (public data)
      this.subscribeToRaces();

      // Only subscribe to session if user is logged in
      if (this.userServices.onLogIn.getValue() == 1) {
        this.subscribeToSession();
      }
    };

    this.wsClient.onDisconnect = () => {
      console.log('WebSocket disconnected');
      this.isConnectedForRaces = false;
      this.isConnectedForSession = false;

      // Stop the cagnotte interval on unexpected disconnect
      this.stopCagnotteInterval();
      this.subscription.unsubscribe();
    };

    this.wsClient.onStompError = (frame) => {
      console.error('STOMP error:', frame);
      // Retry connection after 5 seconds
      setTimeout(() => {
        this.connect();
      }, 5000);
    };

    // Listen for login state changes
    this.userServices.onLogIn.subscribe((data) => {
      if (data) {
        // User logged in - connect if not already connected
        if (!this.wsClient.active) {
          this.connect();
        } else if (this.wsClient.connected) {
          // Already connected, just subscribe to session
          this.subscribeToSession();
        }
      } else {
        // User logged out - disconnect session but keep races
        this.isConnectedForSession = false;
        if (this.wsClient.connected) {
          // Unsubscribe from session-specific subscriptions
          try {
            this.wsClient.unsubscribe('session-subscription');
          } catch (error) {
            console.warn('Session subscription already unsubscribed');
          }
        }
      }
    });
  }

  get message$(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  // Control methods for cagnotte interval
  public stopCagnotteInterval(): void {
    if (this.cagnotteInterval) {
      clearInterval(this.cagnotteInterval);
      this.cagnotteInterval = undefined;
      // console.log('Cagnotte interval stopped');
    }
  }

  public startCagnotteInterval(): void {
    if (!this.cagnotteInterval && this.isConnectedForRaces) {
      const defaultMsg = JSON.stringify(CAGNOTTE_MSG);
      this.cagnotteInterval = setInterval(() => {
        this.processMessage(defaultMsg);
        // console.log('Cagnotte message sent via interval');
      }, 10000);
      // console.log('Cagnotte interval started (10 second repeats)');
    }
  }

  public get isCagnotteIntervalActive(): boolean {
    return this.cagnotteInterval !== undefined;
  }

  // Modern connection method - now public for external calls
  public connect(): void {
    if (!this.wsClient.active) {
      // console.log('Activating WebSocket connection...');
      this.wsClient.activate();
    }
  }

  // Modern disconnection method
  public disconnect(): void {
    this.isConnectedForRaces = false;
    this.isConnectedForSession = false;

    // Stop the cagnotte interval
    this.stopCagnotteInterval();

    if (this.wsClient.active) {
      this.wsClient.deactivate();
    }
  }

  // Subscribe to race updates
  private subscribeToRaces(): void {
    if (this.isConnectedForRaces) return;

    try {
      this.subscription.add(this.wsClient.subscribe(
        `/exchange/flexbet_dashboard/*.*.two_race`,
        (message: IMessage) => {
          this.processMessage(message.body);
        },
        { id: 'races-subscription' }
      ));

      /**
       * SIMULATION: push message with cagnotte races
       * 1. Stop any existing interval
       * 2. Start a new one
       * 3. Send initial message and set up 10-second interval
       * 4. Set isConnectedForRaces to true
       */

      this.isConnectedForRaces = true;
      // this.stopCagnotteInterval();
      // this.startCagnotteInterval();

      // console.log('Subscribed to race updates');
    } catch (error) {
      console.error('Failed to subscribe to races:', error);
    }
  }

  // Subscribe to user session updates
  private subscribeToSession(): void {
    if (this.isConnectedForSession || !this.userServices.userData?.account_code) return;

    try {
      const routingKey = `${RABBIT_CONFIG.QUEUE}.${this.userServices.userData.account_code}`;
      this.subscription.add(this.wsClient.subscribe(
        `/exchange/${RABBIT_CONFIG.EXCHANGE}/${routingKey}`,
        (message: IMessage) => {
          this.processMessage(message.body);
        },
        { id: 'session-subscription' }
      ));

      this.isConnectedForSession = true;
      console.log('Subscribed to session updates for:', this.userServices.userData.account_code);
    } catch (error) {
      console.error('Failed to subscribe to session:', error);
    }
  }

  // Public methods to manually trigger connections (for backward compatibility)
  public connectRaces(): void {
    // Always connect for races (public data doesn't require login)
    this.connect();
  }

  public connectSession(): void {
    // Session requires user to be logged in
    if (this.userServices.onLogIn.getValue() == 1) {
      if (this.wsClient.connected) {
        this.subscribeToSession();
      } else {
        this.connect(); // Will auto-subscribe to session via onConnect handler
      }
    } else {
      console.warn('Cannot connect to session: User not logged in');
    }
  }


  // Process incoming messages (migrated from legacy implementation)
  private async processMessage(rawMsg: string): Promise<void> {
    this.configs.lastmessagetime = new Date();

    let message = JSON.parse(rawMsg);

    // Handle cagnote/jackpot messages
    let cagnotGames: Cagnotte[] = [];
    if (message.hasOwnProperty('message')) {
      this.turfService.load = true;
      this.turfService.cagnoteGames = [];
      cagnotGames = [];

      for (const item of message.message) {
        const cagnote: Cagnotte = { reunioun: '', game: '', amount: '' };
        cagnote.reunioun = item.toString().split('-')[0];
        cagnote.amount = item.toString().split(':')[2];
        const start = item[0].substring(
          item[0].indexOf('[') + 1,
          item[0].indexOf(']')
        );

        cagnote.game = this.configs.games[start.trim().toUpperCase()].trim();
        cagnotGames.push(cagnote);
      }

      // Emit the updated cagnotte list
      this.getCagnote.next(cagnotGames);
    }

    // Handle race course changes

    if (message.hasOwnProperty('tcourse')) {
      this.turfService.courseChange$.next(message.tcourse);
      this.turfService.message = message.tcourse;
    }

    // Handle string messages

    if (typeof message == 'string') message = JSON.parse(message);

    // Handle session expiration
    if (
      message.hasOwnProperty('data') &&
      message.data.hasOwnProperty('isExpired') &&
      message.data.isExpired
    ) {
      this.logger.log('Session expired message received', message.data);

      if (this.userServices.userData.session == message.data.session) {
        if (message.data.isForced) {
          // console.log('Force logout due to session expiration');
          this.userServices.handleSessionExpired();
          this.disconnect();
        } else {
          if (this.userServices.userIdleTime < this.userServices.expireTime) {
            this.userServices.renewSession();
          } else {
            this.userServices.userData = undefined;
            this.disconnect();
          }
        }
      } else {
        this.userServices.logout();
        this.disconnect();
      }
    }

    // Handle balance updates
    if (message.hasOwnProperty('data') && message.type == 'balance') {
      this.userServices.userData.balances = message.data.balances;
    }

    // Emit message to subscribers
    this.messageSubject.next(message);
  }
}
